import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  selfServiceSearchesQuery,
  type SelfServiceSearchesPage as SearchesPayload,
} from '../api/easeedeskSelfService'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * "What people search for" - `Self_service::searches()`, macheta
 * `views/version-3/easeedesk/self_service/searches.php`.
 *
 * Doua liste conteaza aici, si nu sunt cifrele de sus:
 *
 *   CE S-A CAUTAT SI NU S-A GASIT - din asta se scriu articolele urmatoare.
 *   CARE ARTICOLE SE CITESC SI TOT SE AJUNGE LA TICHET - alea se rescriu.
 *
 * Restul e context: cate cautari, cati oameni, ultimele randuri asa cum au
 * fost scrise. Cifrele spun CAT; randurile brute arata CUM scriu oamenii, si
 * asta nu se poate rezuma.
 *
 * Fereastra de timp se schimba din select si cere singura lista noua, ca in
 * macheta veche (`onchange="this.form.submit()"`). Cele trei valori sunt
 * marginite si in API - un `days` inventat cade tot pe treizeci.
 *
 * CE NU S-A MUTAT: butonul de creion de pe lista articolelor cazute deschidea
 * formularul de scris, iar scrisul a ramas in portalul vechi. Nu se deseneaza
 * un buton care nu face nimic; numele articolului spune destul ca sa fie gasit
 * in lista de articole.
 *
 * Cifrele NU se mai urca din zero cu `animateNumber`: acolo erau scrise in
 * `data-number` si un plugin le ridica; aici se scriu direct. Cine se uita la
 * "cate cautari s-au terminat cu mainile goale" nu are nevoie de o animatie.
 */

const STYLES = ['css/adm.css', 'css/self_service.css']

export function SelfServiceSearchesPage() {
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')

  /* Treizeci implicit: destul cat sa se vada un tipar, si nu atat de mult
     incat sa amestece luna trecuta cu ce se intampla acum. */
  const [days, setDays] = useState(30)
  const log = useQuery(selfServiceSearchesQuery(days))

  if (isForbidden(log.error)) return <Forbidden />

  return (
    <Content>
      <div className="selfServiceScreen">
        <header className="panel-heading">
          <h3>
            <strong>What people</strong> search for
          </h3>
          <label className="color">the search log of the shop</label>

          <div className="pull-right">
            <a className="btn btn-default btn-sm" href="/app/self_service">
              <i className="fas fa-book-open"></i> Articles
            </a>
          </div>
        </header>

        {log.isPending ? (
          <Spinner />
        ) : log.isError ? (
          <div className="alert alert-danger" role="alert">
            Something went wrong: {(log.error as Error).message}
          </div>
        ) : !log.data.storage ? (
          <div className="alert alert-warning">
            <i className="fas fa-database"></i> The search log table does not exist yet, so there is nothing to read.
          </div>
        ) : (
          <SearchLog log={log.data} days={days} onDays={setDays} />
        )}
      </div>
    </Content>
  )
}

function SearchLog({ log, days, onDays }: { log: SearchesPayload; days: number; onDays(days: number): void }) {
  const outcome = (code: number) => log.outcomeNames[String(code)] ?? ''

  return (
    <>
      <div className="adm-bar">
        <div className="adm-field">
          <label htmlFor="ssDays">Period</label>
          <select
            id="ssDays"
            className="form-control"
            aria-label="Period"
            value={String(days)}
            onChange={(e) => onDays(Number(e.target.value))}
          >
            {log.periods.map((one) => (
              <option value={String(one)} key={one}>
                Last {one} days
              </option>
            ))}
          </select>
        </div>
      </div>

      {/*
       * Cifrele, si una dintre ele e cea care conteaza: cate cautari s-au
       * terminat cu MAINILE GOALE. Aia nu e o statistica, e lungimea listei de
       * lucruri de scris - de-aia are tonul de avertizare, nu pe cel neutru.
       */}
      <div className="row adm-tiles">
        <Tile tone="blue-hoki" icon="fa-search" number={log.summary.total} label="Searches" />
        <Tile tone="blue-madison" icon="fa-users" number={log.summary.people} label="People" />
        <Tile
          tone={log.summary.empty > 0 ? 'yellow' : 'green-mode'}
          icon="fa-question-circle"
          number={log.summary.empty}
          label="Found nothing"
        />
        <Tile
          tone="brown"
          icon="fa-ticket-alt"
          number={log.summary.outcomes['3'] ?? 0}
          label="Ended in a ticket"
        />
      </div>

      <hr />

      {/*
       * Lista din care se scriu articolele urmatoare. Ordonata dupa cate ori
       * s-a intamplat: un cuvant cautat de treizeci de ori si negasit e o
       * gaura, unul cautat o data e o intamplare.
       */}
      <header className="panel-heading">
        <h3>
          <strong>Searched</strong> and found nothing
        </h3>
        <label className="color">this is the list the next articles are written from</label>
      </header>

      {log.unanswered.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-check-circle"></i> Every search in this period matched something.
        </div>
      ) : (
        <>
          <table className="table table-hover adm-list">
            <thead>
              <tr>
                <th>What they typed</th>
                <th className="text-right" style={{ width: '90px' }}>
                  Times
                </th>
                <th className="adm-nowrap" style={{ width: '170px' }}>
                  Last seen
                </th>
              </tr>
            </thead>
            <tbody>
              {log.unanswered.map((one) => (
                <tr key={one.query}>
                  <td>
                    <strong>{one.query}</strong>
                  </td>
                  <td className="text-right adm-numeric">{one.times}</td>
                  <td className="adm-muted adm-nowrap">{one.last_seen}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-muted">
            Grouped by the exact text, so <em>&bdquo;nu merge imprimanta&rdquo;</em> and{' '}
            <em>&bdquo;Nu merge imprimanta!&rdquo;</em> are two rows. What is kept is what the person actually typed
            &mdash; that is the valuable part.
          </p>
        </>
      )}

      <hr />

      {/*
       * Lista care conteaza cel mai mult. Un articol care se citeste si nu
       * rezolva e mai rau decat unul care lipseste: omul a pierdut si timpul
       * cu el, si a doua oara nu mai citeste niciunul.
       */}
      <header className="panel-heading">
        <h3>
          <strong>Read</strong>, and a ticket opened anyway
        </h3>
        <label className="color">these are the articles to rewrite</label>
      </header>

      {!log.hasArticles ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle"></i> The log does not record <em>which</em> article was opened yet &mdash;
          that column came after the table. Until the migration runs, this list stays empty, which is not the same as
          &bdquo;no article fails&rdquo;.
        </div>
      ) : log.failed.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-check-circle"></i> Nobody read an article and opened a ticket anyway in this period.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>Article</th>
              <th className="text-right" style={{ width: '90px' }}>
                Times
              </th>
              <th className="adm-nowrap" style={{ width: '170px' }}>
                Last seen
              </th>
            </tr>
          </thead>
          <tbody>
            {log.failed.map((one) => (
              <tr key={one.article_id}>
                <td>
                  <strong>{one.title === null ? `article #${one.article_id}` : one.title}</strong>
                  {one.title === null && <span className="adm-muted"> &mdash; deleted, or from another customer</span>}
                </td>
                <td className="text-right adm-numeric">{one.times}</td>
                <td className="adm-muted adm-nowrap">{one.last_seen}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <hr />

      <header className="panel-heading">
        <h3>
          <strong>Latest</strong> searches
        </h3>
        <label className="color">how people actually write, which no number can show</label>
      </header>

      {log.recent.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-search"></i> Nobody has searched in this period.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>What they typed</th>
              <th className="text-right" style={{ width: '90px' }}>
                Articles
              </th>
              <th className="text-right" style={{ width: '90px' }}>
                Services
              </th>
              <th style={{ width: '170px' }}>Then</th>
              <th className="adm-nowrap" style={{ width: '170px' }}>
                When
              </th>
            </tr>
          </thead>
          <tbody>
            {log.recent.map((one, i) => {
              const blank = one.hits_articles === 0 && one.hits_catalog === 0
              return (
                <tr className={blank ? 'adm-muted' : undefined} key={`${one.created_at}-${i}`}>
                  <td>{one.query}</td>
                  <td className="text-right adm-numeric">{one.hits_articles}</td>
                  <td className="text-right adm-numeric">{one.hits_catalog}</td>
                  <td>{outcome(one.outcome)}</td>
                  <td className="adm-muted adm-nowrap">{one.created_at}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </>
  )
}

/** Casuta de sus - markupul `dashboard-stat` din macheta. */
function Tile({ tone, icon, number, label }: { tone: string; icon: string; number: number; label: string }) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className={`dashboard-stat ${tone}`}>
        <div className="visual">
          <i className={`fas ${icon}`}></i>
        </div>
        <div className="details">
          <div className="number">{number}</div>
          <div className="desc">{label}</div>
        </div>
      </div>
    </div>
  )
}
